/**
 * useChangedFiles Hook
 *
 * Extracts changed files from agent messages by detecting Edit/Write tool calls.
 * Used for FileView feature (PRD-UI-001).
 *
 * Optimized to prevent re-renders during streaming:
 * - Uses useRef to track previous paths
 * - Only returns new array reference when files actually change
 *
 * @module hooks/useChangedFiles
 */

import { useMemo, useRef } from 'react';

// Binary file extensions to skip
const BINARY_EXTS = new Set([
  'png', 'jpg', 'jpeg', 'gif', 'ico', 'webp', 'svg', 'pdf',
  'zip', 'tar', 'gz', 'exe', 'dll', 'so', 'dylib', 'woff',
  'woff2', 'ttf', 'eot', 'mp3', 'mp4', 'wav', 'avi', 'mov'
]);

/**
 * Extract file info from a path
 */
function getFileInfo(filePath, tool, toolUseId, timestamp) {
  return {
    path: filePath,
    type: tool === 'Edit' ? 'edit' : 'write',
    toolUseId,
    fileName: filePath.split('/').pop(),
    shortPath: filePath.split('/').slice(-2).join('/'),
    timestamp: timestamp || Date.now()
  };
}

/**
 * Extract changed files from messages
 * @param {Array} messages - Array of agent messages
 * @returns {Array<{path: string, type: 'edit'|'write', toolUseId: string}>}
 */
export function useChangedFiles(messages) {
  // Track previous result to avoid unnecessary re-renders
  const prevResultRef = useRef([]);
  const prevPathsKeyRef = useRef('');

  return useMemo(() => {
    if (!messages || !Array.isArray(messages)) {
      if (prevResultRef.current.length === 0) return prevResultRef.current;
      prevResultRef.current = [];
      prevPathsKeyRef.current = '';
      return prevResultRef.current;
    }

    const filesMap = new Map();

    for (const msg of messages) {
      // Only process tool_use messages for Edit or Write
      if (msg.type !== 'tool_use') continue;
      if (msg.tool !== 'Edit' && msg.tool !== 'Write') continue;

      const filePath = msg.input?.file_path;
      if (!filePath) continue;

      // Skip binary files
      const ext = filePath.split('.').pop()?.toLowerCase();
      if (BINARY_EXTS.has(ext)) continue;

      // Deduplicate by path, keep latest
      const timestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : Date.now();
      filesMap.set(filePath, getFileInfo(filePath, msg.tool, msg.toolUseId, timestamp));
    }

    // Create a stable key from sorted paths to detect actual changes
    const pathsKey = Array.from(filesMap.keys()).sort().join('|');

    // Only update result if paths actually changed
    if (pathsKey === prevPathsKeyRef.current) {
      return prevResultRef.current;
    }

    // Paths changed, update refs and return new array
    prevPathsKeyRef.current = pathsKey;
    prevResultRef.current = Array.from(filesMap.values());
    return prevResultRef.current;
  }, [messages]);
}

export default useChangedFiles;
